import { ActionIcon, Card, Grid, Text } from "@mantine/core";
import {
  showLocalizedDate,
  showLocalizedTime,
} from "../../../../services/dateAndTime/dateAndTimeUtil";
import {
  IconBuilding,
  IconCircleCheck,
  IconFileExport,
  IconVideo,
} from "@tabler/icons";
import { useColors } from "../../../../hooks/colors/useColors";
import { isPhone } from "../../../../constants/env";
import { CancelAppointment } from "./CancelAppointment";
import { isMobile } from "react-device-detect";
import classes from "./appointments.module.css";
import { useAppointmentHelper } from "./useAppointmentHelper";
import { useTranslation } from "next-i18next";
import { AppointmentDTO } from "../../../../hooks/react-query/useAppointmentList";
import { PaymentStatus } from "./payments/PaymentStatus";

type InputProps = {
  appointmentDTO: AppointmentDTO;
  past?: boolean;
};

export function AppointmentItem(props: InputProps) {
  const { colors } = useColors();
  const { appointmentDTO, past } = props;
  const appointment = appointmentDTO.appointment;
  let rightColumnClass = "flex justify-end px-0";
  if (isPhone) rightColumnClass = "flex justify-center";
  const { downloadPresenceDeclaration } = useAppointmentHelper();
  const { t, i18n } = useTranslation("common");
  return (
    <div className="pt-8">
      <Card
        radius="xl"
        className="px-8 py-4"
        key={appointment.id}
        style={{ backgroundColor: colors.primary }}
      >
        <Grid>
          <Grid.Col
            span={{ base: 12, md: 4, lg: 3 }}
            className="flex flex-col justify-center items-center"
          >
            <div className="text-center">
              <Text
                className={
                  isMobile
                    ? "!text-2xl font-bold !text-white"
                    : "!text-3xl font-bold !text-white"
                }
              >
                {showLocalizedDate(appointment.dateTime, i18n.language, "ll")}
                <p className="m-0 !text-white">
                  {showLocalizedTime(appointment.dateTime, i18n.language)}
                </p>
              </Text>
            </div>
            {!appointment.online && (
              <div className="flex gap-4 items-center">
                <IconBuilding color="white" size="3rem" stroke={1} />
                <Text className="!text-3xl font-bold !text-white text-center">
                  {t("appointments.onPremise")}
                </Text>
              </div>
            )}
            {appointment.online && (
              <div className="flex gap-4 items-center">
                <IconVideo color="white" size="3rem" stroke={1} />
                <Text className="!text-3xl font-bold !text-white text-center">
                  {t("appointments.online")}
                </Text>
              </div>
            )}
          </Grid.Col>
          <Grid.Col
            span={{ base: 12, md: 4, lg: 6 }}
            className="flex flex-col justify-center"
          >
            <div className="text-center">
              <Text
                className={
                  isMobile
                    ? "!text-2xl font-bold !text-white"
                    : "!text-3xl font-bold !text-white"
                }
              >
                {appointment.specialist}
              </Text>
              <ul className={classes.noBullets}>
                {appointment.appointmentServiceTypes?.map((service, index) => {
                  return (
                    <li
                      className="!text-2xl !text-white font-bold"
                      key={service.description}
                    >
                      {service.description}
                    </li>
                  );
                })}
              </ul>
            </div>
          </Grid.Col>
          <Grid.Col
            span={{ base: 12, md: 4, lg: 3 }}
            className={rightColumnClass}
          >
            <div className="flex items-center gap-8">
              {past && (
                <div className="flex">
                  {appointment.invoiceId && (
                    <div className="text-center">
                      <ActionIcon size={75}>
                        <IconFileExport size={75} color={colors.cyan} />
                      </ActionIcon>
                      <Text c={colors.cyan}>{t("appointments.invoice")}</Text>
                    </div>
                  )}
                  <div className="flex flex-col items-center">
                    <ActionIcon
                      size={75}
                      color={colors.primary}
                      onClick={() => downloadPresenceDeclaration(appointment)}
                    >
                      <IconFileExport size={75} color={colors.secondary} />
                    </ActionIcon>
                    <Text className="text-center" color={colors.secondary}>
                      {t("appointments.presenceProof")}
                    </Text>
                  </div>
                </div>
              )}
              <div className="flex flex-col items-center w-[200px]">
                <IconCircleCheck color={colors.teal} size="4rem" stroke={1.5} />
                <Text
                  className={
                    isMobile
                      ? "!text-2xl font-bold !text-white"
                      : "!text-3xl font-bold !text-white"
                  }
                >
                  {t(past ? "appointments.done" : "appointments.booked")}
                </Text>
                <PaymentStatus appointmentDTO={appointmentDTO} />
                {!past && <CancelAppointment appointment={appointment} />}
              </div>
            </div>
          </Grid.Col>
        </Grid>
      </Card>
    </div>
  );
}
